<template>
  <f7-page class="public-shop-page" :class="{ 'bg-surface': true, 'font-body-md': true, 'text-on-surface': true }">
    <f7-navbar back-link>
      <f7-nav-title class="font-headline-sm font-bold text-primary">
        {{ shop?.name || $t('shop.boutique') }}
      </f7-nav-title>
    </f7-navbar>

    <AppLayout>
      <template #main>
        <f7-block v-if="loading" class="text-align-center py-12">
          <f7-preloader color="green" />
        </f7-block>

        <template v-else-if="shop">
          <!-- Hero Banner -->
          <f7-card class="hero-banner" padding="false">
            <f7-card-content class="hero-content" padding="false">
              <img
                v-if="shop.bannerUrl"
                :src="shop.bannerUrl"
                class="width-100 h-full object-cover"
                :alt="$t('common.a11y.shopBanner')"
              />
              <div
                v-else
                class="width-100 h-full bg-gradient-to-br from-primary-container via-primary to-surface-container-high display-flex align-items-center justify-content-center"
              >
                <f7-icon material="storefront" :size="48" class="text-on-primary opacity-80" />
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

              <div class="absolute top-3 left-3 display-flex gap-1.5 flex-wrap">
                <f7-badge color="secondary" class="display-flex align-items-center gap-1">
                  <f7-icon material="verified" :size="14" />
                  {{ $t('shopPage.certifiedCoopShop') }}
                </f7-badge>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Brand Profile Strip -->
          <div class="px-gutter-mobile -mt-8 relative z-10 display-flex align-items-flex-end gap-3 pb-3">
            <f7-card class="shop-logo" padding="false">
              <img
                v-if="shop.logoUrl"
                :src="shop.logoUrl"
                class="w-16 h-16 rounded-xl object-cover border-2 border-surface-pure shadow-md bg-surface-container"
                :alt="$t('common.a11y.logo')"
              />
              <div
                v-else
                class="w-16 h-16 rounded-xl bg-surface-container-high border-2 border-surface-pure shadow-md display-flex align-items-center justify-content-center text-primary"
              >
                <f7-icon material="shopping_bag" :size="28" />
              </div>
            </f7-card>

            <div class="flex-1 min-w-0 pb-1">
              <div class="display-flex align-items-center gap-1.5">
                <h1 class="font-headline-sm text-headline-sm font-bold text-on-surface truncate">
                  {{ shop.name }}
                </h1>
                <f7-badge
                  v-if="shop.verified"
                  color="secondary"
                  class="text-secondary flex-shrink-0"
                  style="font-size: 20px; font-variation-settings: 'FILL' 1;"
                >
                  <f7-icon material="verified" :size="20" />
                </f7-badge>
              </div>
              <div class="display-flex align-items-center gap-3 font-label-sm text-label-sm text-outline">
                <span class="display-flex align-items-center gap-1 text-secondary-fixed-dim font-bold">
                  <f7-icon material="star" :size="14" style="font-variation-settings: 'FILL' 1;" />
                  {{ shop.rating || "4.8" }}
                </span>
                <span>•</span>
                <span>{{ shop.completedOrderCount || 0 }} {{ $t('shop.ventes') }}</span>
                <span>•</span>
                <span>{{ products.length }} {{ $t('shop.produits') }}</span>
              </div>
            </div>
          </div>

          <!-- Shop Description -->
          <div
            v-if="shop.description"
            class="px-gutter-mobile py-2 font-body-sm text-body-sm text-on-surface-variant leading-relaxed"
            v-html="shop.description"
          ></div>

          <!-- Action Buttons Strip -->
          <div class="px-gutter-mobile py-2 display-flex align-items-center gap-2">
            <f7-button
              fill
              round
              raised
              class="flex-1 h-11 bg-primary text-on-primary rounded-lg font-label-lg font-bold display-flex align-items-center justify-content-center gap-2 shadow-sm active:scale-[0.99] transition-all"
              @click="followShop"
            >
              <f7-icon material="person_add" :size="18" />
              <span>{{ $t('shop.suivre') }}</span>
            </f7-button>
            <f7-button
              fill
              round
              raised
              class="w-11 h-11 bg-surface-container-high hover:bg-surface-container-highest text-primary rounded-lg display-flex align-items-center justify-content-center active:scale-[0.98] transition-all flex-shrink-0"
              @click="contactSeller"
              aria-label="{{ $t('shopPage.contactSeller') }}"
            >
              <f7-icon material="chat" :size="20" />
            </f7-button>
          </div>

          <!-- Category Filter Chips -->
          <div v-if="categoryList.length > 0" class="width-100 overflow-x-auto no-scrollbar py-3 px-gutter-mobile display-flex gap-2">
            <f7-button
              fill
              round
              small
              raised
              class="filter-chip flex-shrink-0 px-3.5 py-1.5 font-label-md display-flex align-items-center gap-1.5 transition-all"
              :class="!selectedCategory ? 'bg-primary text-on-primary shadow-sm' : 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest'"
              @click="selectedCategory = ''"
            >
              <f7-icon material="grid_view" :size="16" />
              <span>{{ $t('shopPage.allProducts') }}</span>
            </f7-button>
            <f7-button
              v-for="cat in categoryList"
              :key="cat"
              fill
              round
              raised
              small
              class="filter-chip flex-shrink-0 px-3.5 py-1.5 font-label-md display-flex align-items-center gap-1.5 transition-all"
              :class="selectedCategory === cat ? 'bg-primary text-on-primary shadow-sm' : 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest'"
              @click="selectedCategory = cat"
            >
              <f7-icon material="label" :size="16" />
              <span>{{ cat }}</span>
            </f7-button>
          </div>

          <!-- Member Pricing Badge -->
          <f7-card class="mx-gutter-mobile my-2 bg-surface-container-low border border-primary-container/20 p-3 rounded-xl display-flex align-items-center gap-2 text-on-surface-variant font-label-sm text-label-sm">
            <f7-icon material="verified_user" :size="20" class="text-primary flex-shrink-0" />
            <span>{{ $t('shopPage.memberPricingUnlocked') }}</span>
          </f7-card>

          <!-- Product Grid -->
          <div class="product-grid px-gutter-mobile py-2 grid grid-cols-2 gap-3">
            <ProductCard
              v-for="item in filteredProducts"
              :key="item.product.id"
              :product="item.product"
              :media="item.media"
              :variants="item.variants"
              :price-rules="item.priceRules"
              :group-buy-campaign="item.groupBuyCampaign"
              @add="addToCart"
              @view="viewProduct(item)"
              @buy="openDetail(item)"
            />
          </div>

          <div v-if="!filteredProducts.length && !loading" class="text-align-center py-12 text-outline">
            <f7-icon material="shopping_bag" :size="48" class="display-block mb-2 opacity-50" />
            <div class="font-body-md text-body-md">{{ $t('shopPage.noProducts') }}</div>
          </div>

          <!-- CTA Banner for non-members -->
          <div
            v-if="shop.visibility !== 'PUBLIC' && !isMember"
            class="mx-gutter-mobile my-6 bg-surface-container-high rounded-xl p-4 shadow-sm display-flex align-items-center justify-content-space-between gap-3"
          >
            <div class="font-body-sm text-body-sm text-on-surface-variant leading-snug">
              {{ $t('shopPage.joinGroupCta') }}
            </div>
            <f7-button
              fill
              raised
              class="h-10 px-4 bg-primary text-on-primary rounded-lg font-label-md font-bold flex-shrink-0 shadow-sm active:scale-95 transition-all"
              @click="joinGroup"
            >
              {{ $t('shopPage.joinGroup') }}
            </f7-button>
          </div>
        </template>

        <div v-else class="text-align-center py-16 text-outline">
          <span class="material-symbols-outlined" style="font-size: 48px; display: block; margin-bottom: 8px; opacity: 0.5;">visibility_off</span>
          <div class="font-headline-sm text-headline-sm font-semibold">{{ $t('shopPage.shopNotFound') }}</div>
        </div>
      </template>
    </AppLayout>

    <!-- Product detail / offers popup -->
    <ProductDetailPopup
      v-model:opened="detailOpened"
      :item="detailItem"
      :shop-name="shop?.name || ''"
      :shop-id="props.shopId"
      :f7route="f7route"
      :f7router="f7router"
      @add-to-cart="onDetailAddToCart"
    />
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { useShopStore } from "@/js/pinia-stores/shop.js";
import { useMarketplaceProductStore } from "@/js/pinia-stores/marketplaceProduct.js";
import { useCartStore } from "@/js/pinia-stores/cart.js";
import { useAuthStore } from "@/js/pinia-stores/auth.js";
import ProductCard from "@/components/marketplace/ProductCard.vue";
import ProductDetailPopup from "@/components/marketplace/ProductDetailPopup.vue";
import i18n from "../js/i18n.js";

const { t } = i18n.global;
const props = defineProps({
  shopId: { type: String, required: true },
  f7route: Object,
  f7router: Object,
});

const shopStore = useShopStore();
const mpProductStore = useMarketplaceProductStore();
const cartStore = useCartStore();
const authStore = useAuthStore();

const loading = ref(true);
const selectedCategory = ref("");
const shopProducts = ref([]);
const isMember = ref(false);

const shop = computed(() => shopStore.shop);
const products = computed(() => shopStore.products);

const categoryList = computed(() => {
  const cats = new Set();
  shopProducts.value.forEach((p) => {
    if (p.category) cats.add(p.category);
  });
  return [...cats];
});

const filteredProducts = computed(() => {
  if (!selectedCategory.value) return shopProducts.value;
  return shopProducts.value.filter(
    (p) => p.category === selectedCategory.value,
  );
});

onMounted(async () => {
  try {
    await shopStore.loadShop(props.shopId);
    await loadProductDetails();
  } catch (err) {
    f7.toast.show({ text: t("shop.erreurChargement"), closeTimeout: 2000 });
  } finally {
    loading.value = false;
  }
});

async function loadProductDetails() {
  const prods = products.value || [];
  const detailed = [];
  for (const prod of prods) {
    try {
      const [mediaRes, priceRes] = await Promise.all([
        mpProductStore.listMedia(prod.id).catch(() => []),
        mpProductStore.listPriceRules(prod.id).catch(() => []),
      ]);
      detailed.push({
        product: prod,
        media: Array.isArray(mediaRes) ? mediaRes : [],
        variants: [],
        priceRules: Array.isArray(priceRes) ? priceRes : [],
        groupBuyCampaign: prod.groupBuyEnabled
          ? { minimumParticipants: 10, currentParticipants: 0 }
          : null,
      });
    } catch {
      detailed.push({
        product: prod,
        media: [],
        variants: [],
        priceRules: [],
        groupBuyCampaign: null,
      });
    }
  }
  shopProducts.value = detailed;
}

async function addToCart(product) {
  if (!authStore.isAuthenticated) {
    props.f7router.navigate("/login");
    return;
  }
  f7.toast.show({
    text: `${product.name} ${t("shopPage.ajouteAuPanier")}`,
    closeTimeout: 1500,
  });
}

// Detail popup
const detailOpened = ref(false);
const detailItem = ref(null);

function openDetail(item) {
  if (!item?.product) return;
  detailItem.value = item;
  detailOpened.value = true;
}

function viewProduct(item) {
  openDetail(item);
}

function onDetailAddToCart({ product, quantity }) {
  addToCart(product);
  void quantity;
}

function followShop() {
  f7.toast.show({
    text: t("shopPage.followAdded"),
    position: "center",
    closeTimeout: 1500,
  });
}

function contactSeller() {
  f7.toast.show({
    text: t("shopPage.chatComingSoon"),
    position: "center",
    closeTimeout: 2000,
  });
}

function joinGroup() {
  props.f7router.navigate("/groups");
}
</script>

<style scoped>
/* Public Shop Page */
.public-shop-page {
  font-family: 'Work Sans', sans-serif;
}

/* Hero Banner */
.hero-banner {
  margin-bottom: 16px;
  overflow: hidden;
}
.hero-content {
  position: relative;
  z-index: 10;
  height: 176px; /* h-44 = 176px */
}

/* Shop Logo Card */
.shop-logo {
  width: 64px; /* w-16 */
  height: 64px; /* h-16 */
}

/* Filter Chips */
.filter-chip {
  border-radius: 9999px !important; /* round */
  --f7-button-height: 32px;
  --f7-button-font-size: 13px;
  --f7-button-font-weight: 500;
  white-space: nowrap;
}

/* Product Grid */
.product-grid {
  --f7-grid-gap: 12px; /* gap-3 */
}

/* Product Cards */
:deep(.product-card) {
  --f7-card-expandable-font-size: 14px;
  --f7-card-expandable-tablet-width: 100%;
  --f7-card-expandable-tablet-height: 100%;
}

/* Member Pricing Badge */
:deep(.bg-surface-container-low) {
  background-color: var(--avocash-surface-container-low, #f8f9fa);
}

/* Filter chips scroll */
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Empty state */
:deep(.text-outline) {
  color: var(--avocash-outline, #8e8e93);
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .product-grid {
    --f7-grid-gap: 8px;
  }
}
</style>